import React from 'react';
import {
  MdReceipt, MdSend, MdSwapHoriz, MdCameraAlt,
  MdCreditCard, MdLocationOn, MdPhoneInTalk, MdVerifiedUser,
  MdPublic, MdDirectionsCar, MdTrendingUp, MdChevronRight, MdMic, MdChat,
  MdOutlineContactless,
  MdHomeWork, MdAddCircleOutline, MdShowChart, MdRepeat, MdAutorenew,
  MdHelpOutline, MdSettings, MdAccountBalance, MdStars, MdGroups, MdPeople,
  MdFlag, MdPieChart, MdSupportAgent,
} from 'react-icons/md';
import { SWIPE_SEED } from './ChatScreen';

// Composite voice-chat icon: mic with a small chat bubble overlay. Must forward
// `color` to both glyphs — the tile passes color="#fff"; without this the inner
// icons fall back to currentColor and render dark on the tile in light mode.
const VoiceChatIcon = ({ size = 22, color = 'currentColor' }) => (
  <span style={{ position: 'relative', display: 'inline-flex', width: size, height: size, color }}>
    <MdMic size={size} color={color} style={{ display: 'block' }} />
    <MdChat size={Math.round(size * 0.5)} color={color} style={{ position: 'absolute', right: -3, bottom: -2 }} />
  </span>
);

// Three categories — one brand color each, so the section IS the visual cue.
// Order within each group goes most-likely-tapped → least.
const C_BANKING   = '#2FBF00'; // --td-green     — everyday money operations
const C_INVESTING = '#002B1A'; // --td-premium   — wealth, financing products
const C_SERVICES  = '#448889'; // --td-teal      — support, settings, contact

const SERVICE_GROUPS = [
  {
    title: 'Banking',
    items: [
      { Icon: MdSend,             label: 'e-Transfer',    screen: 'emailTransfer',   color: C_BANKING },
      { Icon: MdReceipt,          label: 'Pay Bill',      screen: 'billPayment',     color: C_BANKING },
      { Icon: MdSwapHoriz,        label: 'Transfer',      screen: 'accountTransfer', color: C_BANKING },
      { Icon: MdCameraAlt,        label: 'Deposit',       screen: 'mobileDeposit',   color: C_BANKING },
      { Icon: MdRepeat,           label: 'Pre-Auth',      screen: 'preAuth',         color: C_BANKING },
      { Icon: MdPublic,           label: 'Cross-Border',  screen: 'crossBorder',     color: C_BANKING },
      { Icon: MdCreditCard,       label: 'Manage Card',   screen: 'cardMgmt',        color: C_BANKING },
      { Icon: MdAccountBalance,   label: 'Statements',    screen: 'statements',      color: C_BANKING },
      { Icon: MdAddCircleOutline, label: 'Open Account',  screen: 'openAccount',     color: C_BANKING },
      { Icon: MdGroups,           label: 'Payees',        screen: 'payees',          color: C_BANKING },
      { Icon: MdAutorenew,        label: 'Subscriptions', screen: 'subscriptions',   color: C_BANKING },
    ],
  },
  {
    title: 'Investing',
    items: [
      { Icon: MdTrendingUp,    label: 'Investing',     screen: 'investing',      color: C_INVESTING },
      { Icon: MdPieChart,      label: 'Budget',        screen: 'budgetScreen',   color: C_INVESTING },
      { Icon: MdFlag,          label: 'Goals',         screen: 'goalsScreen',    color: C_INVESTING },
      { Icon: MdShowChart,     label: "Today's Rates", screen: 'todaysRates',    color: C_INVESTING },
      { Icon: MdStars,         label: 'TD Rewards',    screen: 'rewards',        color: C_INVESTING },
      { Icon: MdHomeWork,      label: 'My Mortgage',   screen: 'mortgageDetail', color: C_INVESTING },
      { Icon: MdHomeWork,      label: 'Mortgages',     screen: 'mortgages',      color: C_INVESTING },
      { Icon: MdDirectionsCar, label: 'Auto',          screen: 'autoServices',   color: C_INVESTING },
    ],
  },
  {
    title: 'Services',
    items: [
      { Icon: MdHelpOutline,  label: 'Help',        screen: 'help',       color: C_SERVICES },
      { Icon: MdPeople,       label: 'Advisor',     screen: 'bookAppointment', color: C_SERVICES },
      { Icon: MdSupportAgent, label: 'Match an Advisor', action: 'swipe', color: C_SERVICES },
      { Icon: MdPhoneInTalk,  label: 'In-App Call', screen: 'call',       color: C_SERVICES },
      { Icon: VoiceChatIcon,  label: 'Voice Chat',  screen: 'voiceBot',   color: C_SERVICES },
      { Icon: MdVerifiedUser, label: 'Verify Call', screen: 'callVerify', color: C_SERVICES },
      { Icon: MdLocationOn,   label: 'Locations',   screen: 'locations',  color: C_SERVICES },
      { Icon: MdSettings,     label: 'Settings',    screen: 'settings',   color: C_SERVICES },
    ],
  },
];

const FEATURED = [
  { label: 'Interac e-Transfer', sub: 'Send money to anyone in Canada', screen: 'emailTransfer' },
  { label: 'Pay a Bill',         sub: 'Pay utilities, loans and more',   screen: 'billPayment'   },
  { label: 'Account Transfer',   sub: 'Move funds between your accounts',screen: 'accountTransfer' },
];

const BottomLinkIcon = ({ icon, label, onClick }) => (
  <div className="bottom-link-item" onClick={onClick}>
    {icon}
    <span>{label}</span>
  </div>
);

const ServicesHubScreen = ({ onNavigate, onChatHandoff }) => (
  <div className="screen services-screen bh-flow">
    <header className="bh-flow-hero">
      <div className="bh-flow-hero-left">
        <h1 className="bh-flow-title">Services</h1>
      </div>
    </header>

    <div className="scroll-content gray-bg">

      {/* Grouped service tiles — categories make the long list scannable */}
      {SERVICE_GROUPS.map(group => (
        <section key={group.title} className="services-group">
          <div className="services-section-label">{group.title}</div>
          <div className="services-icon-grid">
            {group.items.map(({ Icon, label, screen, action, color }) => (
              <button
                key={label}
                className="services-icon-tile"
                onClick={() => {
                  // "Match an Advisor" opens the in-chat matching flow (chatSwipeFlow)
                  // directly, via the same chatSeed hand-off other screens use to
                  // pre-load a question — never a screen navigation.
                  if (action === 'swipe') onChatHandoff?.(SWIPE_SEED);
                  else if (screen) onNavigate(screen);
                }}
                disabled={!screen && action !== 'swipe'}
              >
                <div className="services-icon-circle" style={{ background: color }}>
                  <Icon size={22} color="#fff" />
                </div>
                <span>{label}</span>
              </button>
            ))}
          </div>
        </section>
      ))}

      {/* Featured services list */}
      <div className="services-section-label">Popular Services</div>
      <div className="services-featured-list">
        {FEATURED.map(({ label, sub, screen }) => (
          <div
            key={label}
            className="services-featured-item"
            onClick={() => screen && onNavigate(screen)}
            style={{ cursor: screen ? 'pointer' : 'default', opacity: screen ? 1 : 0.5 }}
          >
            <div className="services-featured-text">
              <div className="services-featured-title">{label}</div>
              <div className="services-featured-sub">{sub}</div>
            </div>
            <MdChevronRight size={22} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
          </div>
        ))}
      </div>

      <div className="bottom-links">
        <BottomLinkIcon icon={<MdLocationOn />} label="Find Locations" onClick={() => onNavigate('locations')} />
        <div className="vertical-divider"></div>
        <BottomLinkIcon icon={<MdChat />} label="Chat with us" onClick={() => onNavigate('chat')} />
        <div className="vertical-divider"></div>
        <BottomLinkIcon icon={<MdOutlineContactless />} label="Contact Us" />
      </div>

    </div>
  </div>
);

export default ServicesHubScreen;
